import { Attr } from './attributes';
import { capturesFullPayloads, type EnvBag } from './environment';
import { annotateActiveSpan } from './withSpan';

/**
 * Describe a request body without carrying its values.
 *
 * Request bodies never reach a span on their own — Next and @vercel/otel do not
 * capture them, and routes parse them internally with `await req.json()`. So
 * the choice is not "scrubbed body vs raw body", it is "nothing vs something".
 *
 * This emits the something: which keys were sent, which filter FIELDS were used,
 * how many, and the pagination window. That answers most of what you actually
 * ask a trace ("why did this search return nothing?", "why was this one slow?",
 * "is the client sending the filter I expect?") while carrying no customer data.
 *
 * Filter VALUES are deliberately excluded — they are company names, person
 * names, domains and email addresses.
 */

const MAX_KEYS = 30;
const MAX_FIELD_CHARS = 480;

/** Field-name accessors, covering the shapes used across our filter payloads. */
function filterFieldName(entry: unknown): string | undefined {
  if (!entry || typeof entry !== 'object') return undefined;
  const candidate = entry as Record<string, unknown>;
  for (const key of ['field', 'key', 'name', 'identifier', 'type']) {
    const value = candidate[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}

function collectFilterFields(value: unknown, depth = 0): string[] {
  if (depth > 3 || !value) return [];

  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      const name = filterFieldName(entry);
      // Nested groups (AND/OR trees) carry their children under `filters`.
      const nested = entry && typeof entry === 'object' ? (entry as Record<string, unknown>).filters : undefined;
      return [...(name ? [name] : []), ...collectFilterFields(nested, depth + 1)];
    });
  }

  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>);
  }

  return [];
}

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)) ? Number(value) : undefined;

/**
 * Build PII-free span attributes describing a parsed request body.
 * Returns an empty object for anything that is not a plain object.
 */
export function describeRequestBody(body: unknown): Record<string, string | number | boolean> {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return {};

  const record = body as Record<string, unknown>;
  const attributes: Record<string, string | number | boolean> = {};

  // Which keys the client sent. Names only — a missing key is often the bug.
  const keys = Object.keys(record).slice(0, MAX_KEYS).sort();
  if (keys.length > 0) attributes['firmable.request.keys'] = keys.join(',');

  const fields = Array.from(new Set(collectFilterFields(record.filters))).sort();
  if (fields.length > 0) {
    attributes[Attr.SEARCH_FILTER_KEYS] = fields.join(',').slice(0, MAX_FIELD_CHARS);
    attributes['firmable.request.filter_count'] = fields.length;
  }

  const from = asNumber(record.from);
  const size = asNumber(record.size ?? record.limit);
  if (from !== undefined) attributes['firmable.request.from'] = from;
  if (size !== undefined) attributes['firmable.request.size'] = size;

  // Low-cardinality enums that are safe and highly diagnostic.
  for (const key of ['category', 'profileType', 'usageEventKey', 'crm', 'country'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.length <= 64) attributes[`firmable.request.${key}`] = value;
  }

  // Batch sizes — the usual cause of a slow or rejected request.
  for (const key of ['ids', 'items', 'profileIds', 'profiles'] as const) {
    const value = record[key];
    if (Array.isArray(value)) attributes[`firmable.request.${key}_count`] = value.length;
  }

  if (Array.isArray(record.sort)) {
    const sortFields = record.sort
      .map((entry) => filterFieldName(entry))
      .filter((name): name is string => Boolean(name));
    if (sortFields.length > 0) attributes['firmable.request.sort_fields'] = sortFields.join(',').slice(0, 200);
  }

  return attributes;
}

/**
 * Describe query-string parameters: names always, values only for the
 * low-cardinality pagination/scoping keys that cannot identify a person.
 */
const SAFE_QUERY_VALUE_KEYS = new Set(['from', 'size', 'limit', 'offset', 'page', 'country', 'category', 'type', 'tab', 'sort', 'order']);

export function describeSearchParams(params: URLSearchParams): Record<string, string | number> {
  const attributes: Record<string, string | number> = {};
  const names: string[] = [];

  params.forEach((value, key) => {
    names.push(key);
    if (SAFE_QUERY_VALUE_KEYS.has(key.toLowerCase()) && value.length <= 64) {
      attributes[`firmable.request.${key}`] = value;
    }
  });

  const unique = Array.from(new Set(names)).sort();
  if (unique.length > 0) attributes['url.query_keys'] = unique.join(',');

  return attributes;
}

/**
 * Max serialised body kept on a span outside production.
 *
 * Generous enough for a real search payload, bounded so a 10k-id bulk request
 * cannot blow past Logfire's 10MB span limit or the per-attribute cap.
 */
export const MAX_DEBUG_BODY_CHARS = 8_000;

/** JSON.stringify that survives circular references and BigInt. */
function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  try {
    const json = JSON.stringify(
      value,
      (_key, item) => {
        if (typeof item === 'bigint') return item.toString();
        if (item && typeof item === 'object') {
          if (seen.has(item as object)) return '[circular]';
          seen.add(item as object);
        }
        return item;
      },
    );
    if (json === undefined) return '';
    return json.length > MAX_DEBUG_BODY_CHARS ? `${json.slice(0, MAX_DEBUG_BODY_CHARS)}…[truncated]` : json;
  } catch {
    return '[unserialisable]';
  }
}

/**
 * Span attributes describing a request body — inbound to a route, or outbound
 * to a dependency like the Search API.
 *
 * Always records the PII-free shape. Outside production it *also* records the
 * raw body under `firmable.debug.request_body`, because in dev, preview and
 * staging the payload is test data and a full body beats a summary when you are
 * debugging.
 *
 * The `firmable.debug.*` namespace is deliberate: `firmable.*` is treated as
 * curated-and-safe by the scrubber, so this value passes through verbatim. That
 * makes the environment gate the only thing standing between a raw body and
 * Logfire — which is why it lives here at the emission site rather than in the
 * scrubber, and why `capturesFullPayloads` is a hard `!== 'prod'` check.
 */
export function requestBodyAttributes(body: unknown, env: EnvBag = process.env): Record<string, string | number | boolean> {
  const attributes: Record<string, string | number | boolean> = describeRequestBody(body);

  if (capturesFullPayloads(env)) {
    const serialised = safeStringify(body);
    if (serialised) attributes['firmable.debug.request_body'] = serialised;
  }

  return attributes;
}

export function annotateRequestBody(body: unknown, env: EnvBag = process.env): void {
  const attributes = requestBodyAttributes(body, env);
  if (Object.keys(attributes).length > 0) annotateActiveSpan(attributes);
}

/**
 * Parse a JSON request body and annotate the active span in one step.
 *
 * A drop-in replacement for `await req.json()`. Routes parse their own bodies,
 * so there is no shared handler seam to hook — and asking every author to
 * remember a second `annotateRequestBody(...)` line means the telemetry is
 * always one route behind whatever someone is currently debugging. Folding it
 * into the parse makes instrumentation the default rather than a chore.
 *
 * Errors propagate unchanged: a malformed body must still reject exactly as
 * `req.json()` would, so existing try/catch behaviour is untouched.
 *
 * The generic defaults to `any`, matching `req.json()`, so this stays a true
 * drop-in. Defaulting to `unknown` would be safer in the abstract but would
 * force type annotations onto ~30 unrelated call sites — tightening request
 * typing is a worthwhile change, just not one to smuggle in with telemetry.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function readJsonBody<T = any>(
  req: { json: () => Promise<unknown> },
  env: EnvBag = process.env,
): Promise<T> {
  const body = await req.json();

  try {
    annotateRequestBody(body, env);
  } catch {
    // Telemetry must never turn a valid request into a failed one.
  }

  return body as T;
}

/**
 * Query-string equivalent of `requestBodyAttributes`.
 *
 * Same policy as bodies: the PII-free shape always, and outside production the
 * raw query too, because there the values are test data and a full query beats
 * a summary when you are debugging. Emitted centrally by
 * `RequestShapeSpanProcessor` — routes do not call this.
 *
 * No `annotateSearchParams` counterpart on purpose. A body has to be parsed by
 * the route, which is why `readJsonBody` exists; a query string is already on
 * the request, so making 79 call sites remember a helper would repeat exactly
 * the mistake that left only six routes reporting their bodies.
 */
export function searchParamsAttributes(
  params: URLSearchParams,
  rawQuery: string,
  env: EnvBag = process.env,
): Record<string, string | number | boolean> {
  const attributes: Record<string, string | number | boolean> = describeSearchParams(params);

  if (rawQuery && capturesFullPayloads(env)) {
    attributes['firmable.debug.query'] = rawQuery.slice(0, MAX_DEBUG_BODY_CHARS);
  }

  return attributes;
}
